import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'quiet'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  /** Fills the width of its container. Used for the main action on a phone. */
  block?: boolean
  /**
   * Declared, because `ButtonHTMLAttributes` does not carry it.
   *
   * React 19 passes `ref` through as an ordinary prop, so it reaches the
   * element on the spread below without anything else changing — but the type
   * has to say so, the same way the memory and sudoku cards do.
   */
  ref?: Ref<HTMLButtonElement>
  children: ReactNode
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-on-primary hover:bg-primary-hover border-transparent',
  secondary: 'bg-surface-raised text-ink border-border hover:bg-surface-sunken',
  quiet: 'bg-transparent text-ink-muted border-transparent hover:bg-surface-sunken',
}

export function Button({ variant = 'primary', block = false, ...props }: ButtonProps) {
  return (
    <button
      type="button"
      {...props}
      className={[
        'inline-flex items-center justify-center gap-2 border-2 font-display font-medium',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        // min-h/min-w rather than padding alone: the tap target stays large
        // enough even when the label is a single character.
        'min-h-(--size-tap) min-w-(--size-tap) rounded-(--radius-control) px-5',
        VARIANTS[variant],
        block ? 'w-full' : '',
        props.className ?? '',
      ].join(' ')}
    />
  )
}
